import { DETECTION_RULES, type RuleCategory } from "./policy-data";
import { humanizeRuleId, LLM_ANALYZER_DEAD_LETTER_RULE_ID } from "./rule-ids";

const SOURCE_TO_CATEGORY: ReadonlyMap<string, RuleCategory> = new Map<
  string,
  RuleCategory
>([
  ["destructive_tool", "destructive_tool"],
  ["shadow_mcp", "shadow_mcp"],
  ["prompt_injection", "prompt_injection"],
  ["llm_judge", "prompt_policy"],
  ["cli_destructive", "cli_destructive"],
  ["account_identity", "account_identity"],
  // Scanner-source fallbacks: when a rule_id doesn't carry its category
  // prefix (e.g. gitleaks' bare "generic-api-key"), classify by source so
  // we never leak the scanner name to the UI. Keep in sync with the Go
  // classifier in server/internal/risk/categories.
  ["gitleaks", "secrets"],
  ["presidio", "pii"],
]);

// The judge-backed detectors. Each owns exactly one rule whose humanized id
// restates its own category ("Prompt Injection" under a "Prompt Injection"
// badge), so the rule label adds nothing for these findings. Their
// `description` carries the judge's per-finding rationale instead of a static
// rule blurb, and their `match` is the whole flagged event rather than a
// substring.
export const JUDGE_SOURCES = ["llm_judge", "prompt_injection"] as const;

const JUDGE_SOURCE_SET: ReadonlySet<string> = new Set(JUDGE_SOURCES);

export function isJudgeSource(source: string | undefined): boolean {
  return source !== undefined && JUDGE_SOURCE_SET.has(source);
}

// The fine-tuned LLM risk analyzer. One model call evaluates every risk the
// scanner-backed detectors cover, and each finding it writes carries a
// `<category>.llm` rule id, the model's reasoning as `description`, and no
// `match` (it reports no spans). It is deliberately NOT a judge source: its
// rule ids classify to the same categories the scanners feed, and a rule
// exclusion on one silences a single category rather than the whole engine,
// so the rule label and the exclusion affordances stay on.
export const LLM_ANALYZER_SOURCE = "llm_analyzer";

export function isLlmAnalyzerSource(source: string | undefined): boolean {
  return source === LLM_ANALYZER_SOURCE;
}

// Findings whose evidence is a per-finding rationale in `description` rather
// than a span of the message: the judge detectors and the LLM analyzer. The
// evidence surfaces render these through the rationale path (EventMatchDialog)
// instead of the masked-match chip, which would show a placeholder over a
// match that does not exist.
export function isRationaleSource(source: string | undefined): boolean {
  return isJudgeSource(source) || isLlmAnalyzerSource(source);
}

// LLM analyzer rule id → category. Keep in sync with the RuleIDs the Go
// classifier lists in server/internal/risk/categories: the dead-letter
// sentinel has no category of its own there and falls through to `custom`.
const LLM_ANALYZER_RULE_CATEGORY: ReadonlyMap<string, RuleCategory> = new Map<
  string,
  RuleCategory
>([
  ["secret.llm", "secrets"],
  ["pii.llm", "pii"],
  ["prompt_injection.llm", "prompt_injection"],
  ["destructive_tool.llm", "destructive_tool"],
  ["cli_destructive.llm", "cli_destructive"],
  [LLM_ANALYZER_DEAD_LETTER_RULE_ID, "custom"],
]);

// Shadow MCP is the documented carve-out to redaction: its "match" is a server
// URL or command identifier, not captured user content, so the server passes
// match_redacted through verbatim. Surfaces that would otherwise describe the
// value as hidden have to check this first — the identifier is right there on
// screen.
export function isShadowMcpSource(source: string | undefined): boolean {
  return source === "shadow_mcp";
}

// A signal clusters findings on one rule, so a judge-backed signal is one
// whose detection sources are judge detectors. Judge findings cannot be
// meaningfully excluded — every one carries the same constant rule id, so a
// rule exclusion would silence the whole detector — which is why exclusion
// affordances hide behind this check and false-positive dismissal takes over.
export function hasJudgeSource(sources: readonly string[]): boolean {
  return sources.some(isJudgeSource);
}

// A signal whose every detection source reports a rationale rather than a
// match renders all its evidence through the rationale path, so the
// panel-wide reveal-all toggle (which only drives MaskedMatch rows) would
// have nothing to act on. Empty sources are not "only rationale": the
// evidence surface then keeps its default redacted rendering.
export function hasOnlyRationaleSources(sources: readonly string[]): boolean {
  return sources.length > 0 && sources.every(isRationaleSource);
}

// Whether an evidence row names the rule beside its category code. Judge and
// LLM analyzer category rules restate the category ("secret.llm" under
// SECRETS), so only the code shows for them — except the analyzer's
// dead-letter sentinel, which classifies to `custom` and would otherwise
// leave no hint that the analysis never ran. Scanner rules always show.
export function evidenceShowsRuleTitle(
  source: string | undefined,
  ruleId: string | undefined,
): boolean {
  return (
    !isRationaleSource(source) || ruleId === LLM_ANALYZER_DEAD_LETTER_RULE_ID
  );
}

const ruleIdToCategory = new Map<string, RuleCategory>();
const ruleIdToTitle = new Map<string, string>();

for (const [category, rules] of Object.entries(DETECTION_RULES)) {
  for (const rule of rules) {
    ruleIdToCategory.set(rule.id, category as RuleCategory);
    ruleIdToTitle.set(rule.id, rule.title);
  }
}

// DETECTION_RULES.id is the canonical rule_id the backend writes to
// risk_results, so lookup maps key by it directly.
const RULE_ID_TO_CATEGORY: ReadonlyMap<string, RuleCategory> = ruleIdToCategory;
const RULE_ID_TO_TITLE: ReadonlyMap<string, string> = ruleIdToTitle;

export function getRuleTitleFallback(ruleId: string | undefined): string {
  if (!ruleId) return "-";
  return RULE_ID_TO_TITLE.get(ruleId) ?? humanizeRuleId(ruleId);
}

// ── Severity (CVSS-style) ────────────────────────────────────────────────────
// A policy carries a decimal severity score (0.1–10). Findings resolve their
// rating from the owning policy's score at read time. Bands mirror the CVSS
// qualitative scale, minus the "None" band — the lowest rating is Low.
export type SeverityRating = "low" | "medium" | "high" | "critical";

const SEVERITY_BANDS: { min: number; rating: SeverityRating }[] = [
  { min: 9.0, rating: "critical" },
  { min: 7.0, rating: "high" },
  { min: 4.0, rating: "medium" },
  { min: 0, rating: "low" },
];

export function scoreToRating(score: number): SeverityRating {
  for (const band of SEVERITY_BANDS) {
    if (score >= band.min) return band.rating;
  }
  return "low";
}

export const SEVERITY_RATING_LABEL: Record<SeverityRating, string> = {
  low: "Low",
  medium: "Medium",
  high: "High",
  critical: "Critical",
};

export function getCategoryForFinding(
  source?: string,
  ruleId?: string,
): RuleCategory | null {
  if (ruleId) {
    const byRule =
      RULE_ID_TO_CATEGORY.get(ruleId) ?? LLM_ANALYZER_RULE_CATEGORY.get(ruleId);
    if (byRule) return byRule;
  }
  if (source) {
    return SOURCE_TO_CATEGORY.get(source) ?? null;
  }
  return null;
}

// getCategoryCodeForFinding resolves a finding to a short, uppercase,
// customer-safe category code (e.g. "SECRETS", "PII") for use as compact
// fine-print or badge text. It NEVER returns the raw scanner `source`
// (`gitleaks`, `presidio`), which is an implementation detail we don't
// expose. Falls back to "FLAGGED" when nothing classifies.
export function getCategoryCodeForFinding(
  source?: string,
  ruleId?: string,
): string {
  const category = getCategoryForFinding(source, ruleId);
  return category ? category.toUpperCase() : "FLAGGED";
}
